/**
 * Customer Authentication Service — Passwordless Email OTP
 *
 * Customers authenticate by entering their email and receiving a one-time
 * code. No passwords, no MFA setup, no Cognito SDK required.
 *
 * Flow: enter email → receive code via email → enter code → authenticated
 */

import axios from 'axios';
import { mockCustomerAuthService } from './auth.mock';

const useMockAuth = import.meta.env.VITE_USE_MOCK_AUTH === 'true';

const TOKEN_STORAGE_KEY = 'customerTokens';
const API_BASE_URL = import.meta.env.VITE_API_ENDPOINT || '/api';

interface StoredTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

function storeTokens(data: {
  id_token: string;
  access_token: string;
  refresh_token: string;
  expires_in: number;
}): void {
  const tokens: StoredTokens = {
    idToken: data.id_token,
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresAt: Date.now() + data.expires_in * 1000,
  };
  localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
}

function getStoredTokens(): StoredTokens | null {
  const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

export interface RequestCodeResult {
  success: boolean;
  error?: string;
}

export interface VerifyCodeResult {
  success: boolean;
  error?: string;
}

const realAuthService = {
  /**
   * Request a one-time login code sent to the customer's email.
   */
  async requestCode(email: string): Promise<RequestCodeResult> {
    try {
      await axios.post(`${API_BASE_URL}/customer/auth/request-code`, { email });
      return { success: true };
    } catch {
      return { success: false, error: 'Failed to send login code. Please try again.' };
    }
  },

  /**
   * Verify a one-time login code and store the resulting tokens.
   */
  async verifyCode(email: string, code: string): Promise<VerifyCodeResult> {
    try {
      const response = await axios.post(`${API_BASE_URL}/customer/auth/verify-code`, {
        email,
        code,
      });
      storeTokens(response.data);
      return { success: true };
    } catch {
      return { success: false, error: 'Invalid or expired code. Please try again.' };
    }
  },

  /**
   * Refresh tokens using the stored refresh token.
   */
  async refreshTokens(): Promise<boolean> {
    const tokens = getStoredTokens();
    if (!tokens?.refreshToken) return false;

    try {
      const response = await axios.post(`${API_BASE_URL}/customer/auth/refresh`, {
        refresh_token: tokens.refreshToken,
      });
      storeTokens(response.data);
      return true;
    } catch {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      return false;
    }
  },

  logout(): void {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    window.location.href = '/login';
  },

  async getIdToken(): Promise<string | null> {
    const tokens = getStoredTokens();
    if (!tokens) return null;

    // If token is expired, try to refresh
    if (tokens.expiresAt < Date.now()) {
      const refreshed = await this.refreshTokens();
      if (!refreshed) return null;
      const newTokens = getStoredTokens();
      return newTokens?.idToken || null;
    }

    return tokens.idToken;
  },

  async isAuthenticated(): Promise<boolean> {
    const token = await this.getIdToken();
    return token !== null;
  },

  getTokenExpiryTime(): number | null {
    const tokens = getStoredTokens();
    return tokens?.expiresAt || null;
  },
};

export const customerAuthService = useMockAuth ? mockCustomerAuthService : realAuthService;
